import { styled } from "styled-components";

import { triggerLogin } from "authentication/actions";
import { LockIcon } from "common/components/icons";
import { UIIconButton } from "common/components/UIIconButton";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { TrackPage } from "domains/analytics/types";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { useGoToMembership } from "entities/user/utils";

const StyledLockedContent = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-bottom: 1rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledButton = styled(UIIconButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledCircle = styled.div`
  position: absolute;
  bottom: 24px;
  left: 30px;
  width: 40px;
  height: 40px;
  border-width: 2px;
  border-radius: 22px;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.lockedLabelBackground};
`;

const StyledIcon = styled(LockIcon)`
  position: absolute;
  bottom: 10px;
  left: 10px;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.lockedLabelBackground};
`;

type LockedContentProps = { isFirstLocked?: boolean; trackPage: TrackPage };

export const LockedContent = ({ trackPage, isFirstLocked = true }: LockedContentProps) => {
  const dispatch = useDispatch();
  const goToMembership = useGoToMembership();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const track = useTrack();
  const translate = useTranslate();

  const onClick = () => {
    if (isAuthenticated) {
      track({ name: "Upgrade", page: trackPage });
      goToMembership();
    } else dispatch(triggerLogin());
  };

  return (
    <StyledLockedContent onClick={onClick} data-test-id="locked-section">
      {isFirstLocked ? (
        <StyledButton icon="lock" variant="outlined">
          {translate(isAuthenticated ? "app.topic.locked_content.upgrade" : "app.topic.locked_content")}
        </StyledButton>
      ) : (
        <StyledCircle>
          <StyledIcon />
        </StyledCircle>
      )}
    </StyledLockedContent>
  );
};
